import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  CogIcon,
  ShieldCheckIcon,
  CurrencyYenIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

import { API_BASE } from '../../lib/config';

const BACKEND_URL = API_BASE;

const SystemSettings = ({ onBack }) => {
  const [settings, setSettings] = useState({
    general: {},
    fees: {},
    trust_score: {},
    blocked_keywords: {}
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [tempValues, setTempValues] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      // BACKEND_URL is API_BASE which includes /api. 
      // Correct path: /api/admin/settings
      const response = await axios.get(`${BACKEND_URL}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Group settings by category
      const groupedSettings = response.data.reduce((acc, setting) => {
        if (!acc[setting.category]) {
          acc[setting.category] = {};
        }
        acc[setting.category][setting.key] = setting.value;
        return acc;
      }, {});

      setSettings({
        general: groupedSettings.general || {},
        fees: groupedSettings.fees || {},
        trust_score: groupedSettings.trust_score || {},
        blocked_keywords: groupedSettings.blocked_keywords || {}
      });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      // Fallback
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSetting = async (category, key, value) => {
    try {
      const token = localStorage.getItem('admin_token');
      let payloadValue = value;

      // Special handling for platform_fee_rate: Input % (0-100) -> Output Decimal (0.0-1.0)
      if (key === 'platform_fee_rate') {
        const floatVal = parseFloat(value);
        if (!isNaN(floatVal)) {
          payloadValue = floatVal / 100.0;
        } else {
          // Invalid input, maybe keep old or default?
          toast.error('有効な数値を入力してください');
          return;
        }
      }

      await axios.put(`${BACKEND_URL}/admin/settings/${key}`, {
        value: payloadValue,
        category,
        description: `${category} - ${key}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSettings(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [key]: payloadValue
        }
      }));

      // Update the temp value to match input if needed, but usually we just clear temp
      // However, for decimal->percent, we want to keep the percentages visible

      toast.success('設定を更新しました');
    } catch (error) {
      console.error('Failed to update setting:', error);
      toast.error('設定の更新に失敗しました');
    }
  };

  const handleSave = (category, key) => {
    const value = tempValues[`${category}_${key}`];
    if (value !== undefined) {
      handleUpdateSetting(category, key, value);
      // We don't delete tempValues immediately here because if it's a transformed value
      // we want to keep showing the transformed input.
      // Actually, if we update state 'settings' with decimal, the render logic will convert it back to %
      // so clearing tempValues is correct IF the render logic handles the new 'settings' value correctly.
      setTempValues(prev => {
        const newTemp = { ...prev };
        delete newTemp[`${category}_${key}`];
        return newTemp;
      });
    }
  };

  const tabs = [
    { id: 'general', label: '基本設定', icon: <CogIcon className="w-5 h-5" /> },
    { id: 'fees', label: '手数料・料金', icon: <CurrencyYenIcon className="w-5 h-5" /> },
    { id: 'trust_score', label: '信頼スコア', icon: <ShieldCheckIcon className="w-5 h-5" /> },
    { id: 'blocked_keywords', label: 'ブロック設定', icon: <ExclamationTriangleIcon className="w-5 h-5" /> }
  ];

  const renderSettingField = (category, key, label, type = 'text', placeholder = '', isPercentage = false) => {
    let currentValue = settings[category]?.[key] || '';

    // Transform decimal to percentage for display
    if (key === 'platform_fee_rate' && currentValue !== '') {
      // Check if it's already percentage (large number) or decimal? 
      // Backend sends 0.18. Frontend should show 18.
      const floatVal = parseFloat(currentValue);
      if (!isNaN(floatVal) && floatVal <= 1.0) {
        currentValue = (floatVal * 100).toFixed(1).replace(/\.0$/, '');
      }
    }

    const tempKey = `${category}_${key}`;
    const displayValue = tempValues[tempKey] !== undefined ? tempValues[tempKey] : currentValue;
    const hasChanges = tempValues[tempKey] !== undefined && tempValues[tempKey] !== currentValue;

    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <div className="flex items-center space-x-2">
          {type === 'textarea' ? (
            <textarea
              rows="4"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={displayValue}
              onChange={(e) => setTempValues(prev => ({ ...prev, [tempKey]: e.target.value }))}
              placeholder={placeholder}
            />
          ) : (
            <input
              type={type}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={displayValue}
              onChange={(e) => setTempValues(prev => ({ ...prev, [tempKey]: e.target.value }))}
              placeholder={placeholder}
            />
          )}
          {hasChanges && (
            <button
              onClick={() => handleSave(category, key)}
              className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
            >
              <CheckCircleIcon className="w-4 h-4 mr-1" />
              保存
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">基本設定</h3>
            {renderSettingField('general', 'site_name', 'サイト名', 'text', 'バイターズ')}
            {renderSettingField('general', 'site_description', 'サイト説明', 'text', '即日バイトマッチングアプリ')}
            {renderSettingField('general', 'contact_email', '問い合わせメールアドレス', 'email', 'support@byters.jp')}
            {renderSettingField('general', 'max_applications_per_job', '求人あたりの最大応募数', 'number', '10')}
          </div>
        );

      case 'fees':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">手数料・料金設定</h3>
            {/* Link to platform_fee_rate logic */}
            {renderSettingField('fees', 'platform_fee_rate', 'プラットフォーム手数料 (%)', 'number', '18')}
            {/* Link to minimum_wage logic */}
            {renderSettingField('fees', 'minimum_wage', '最低時給 (円)', 'number', '900')}
            {renderSettingField('fees', 'payment_processing_fee', '決済手数料 (%)', 'number', '3.6')}
          </div>
        );

      case 'trust_score':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">信頼スコア設定</h3>
            {renderSettingField('trust_score', 'complete_job_points', '仕事完了ポイント', 'number', '10')}
            {renderSettingField('trust_score', 'good_rating_points', '高評価獲得ポイント', 'number', '5')}
            {renderSettingField('trust_score', 'verification_points', '本人確認完了ポイント', 'number', '20')}
          </div>
        );

      case 'blocked_keywords':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">ブロック設定</h3>
            {renderSettingField('blocked_keywords', 'prohibited_words', '禁止キーワード (カンマ区切り)', 'textarea', 'ギャンブル,違法,詐欺,マルチ商法')}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 mr-2" />
                <div>
                  <h4 className="text-sm font-medium text-yellow-800">注意</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    ここで設定したキーワードを含む求人は自動的に非表示になります。
                    法的制限に基づいて適切に設定してください。
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* 戻るボタン */}
      {onBack && (
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            メニューに戻る
          </button>
        </div>
      )}

      {/* ヘッダー */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">システム設定</h2>
        <div className="flex items-center space-x-2">
          <div className="flex items-center text-sm text-gray-500">
            <CheckCircleIcon className="w-4 h-4 mr-1" />
            最終更新: {new Date().toLocaleString('ja-JP')}
          </div>
        </div>
      </div>

      {/* タブナビゲーション */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              {tab.icon}
              <span className="ml-2">{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* コンテンツエリア */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          renderTabContent()
        )}
      </div>

      {/* バックアップ・復元 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">設定の管理</h3>
        <div className="flex space-x-4">
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            設定をエクスポート
          </button>
          <button className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors">
            設定をインポート
          </button>
          <button className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors">
            デフォルトに戻す
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;